import React, { useState, useEffect, useCallback } from 'react';
import Page from '../../components/Page';
import { Icon, Breadcrumb, Table, Button, Segment, Dropdown, Label } from 'semantic-ui-react';
import { Link, useParams } from 'react-router-dom';
import MyMenu from './menu';
import AccountProgress from '../../components/AccountProgress';
import { get, del } from '../../util/api';
import { useTranslation } from 'react-i18next';

const roleColor = (role) => {
	switch (role) {
		case 'owner': return 'purple';
		case 'admin': return 'blue';
		case 'agent': return 'green';
		case 'standard': return 'teal';
		case 'dev': return 'orange';
		case 'viewer': return 'yellow';
		default: return 'grey';
	}
};

const deviceIcon = (device) => {
	if (device === 'mobile') return 'mobile alternate';
	if (device === 'tablet') return 'tablet alternate';
	return 'desktop';
};

export default function AgentSessions() {
	const { t } = useTranslation();
	const { uid: paramUid } = useParams();

	const [counts, setCounts] = useState([]);
	const [sessions, setSessions] = useState([]);
	const [selectedUid, setSelectedUid] = useState(paramUid || '');
	const [loading, setLoading] = useState(true);
	const [terminating, setTerminating] = useState(null);

	const fetchCounts = useCallback(async () => {
		try {
			const res = await get('/live/agent/sessionCounts');
			if (res.success) setCounts(res.data);
		} catch (_) {}
	}, []);

	const fetchSessions = useCallback(async (uid) => {
		setLoading(true);
		try {
			const res = await get('/live/agent/sessionDetails', uid ? { uid } : undefined);
			if (res.success) setSessions(res.data);
		} catch (_) {}
		setLoading(false);
	}, []);

	useEffect(() => {
		fetchCounts();
		fetchSessions(selectedUid);
	}, []);

	const handleFilterChange = (e, { value }) => {
		setSelectedUid(value);
		fetchSessions(value);
	};

	const handleTerminate = async (uid, sid) => {
		setTerminating(sid);
		try {
			await del(`/live/agent/sessions/${uid}/${sid}`);
			setSessions((prev) => prev.filter((s) => s.sid !== sid));
			setCounts((prev) => prev.map((a) => (String(a.uid) === String(uid) ? { ...a, sessionCount: Math.max(0, a.sessionCount - 1) } : a)));
		} catch (_) {}
		setTerminating(null);
	};

	const agentOptions = [
		{ text: t('allAgents'), value: '' },
		...counts.map((a) => ({
			text: `${a.email} (${a.sessionCount})`,
			value: String(a.uid),
		})),
	];

	return (
		<Page
			header={
				<Breadcrumb size="big">
					<Breadcrumb.Section as={Link} to="/settings">
						<Icon name="setting" />
						{t('settings')}
					</Breadcrumb.Section>
					<Breadcrumb.Divider icon="right chevron" />
					<Breadcrumb.Section as={Link} to="/settings/agents">
						{t('agents')}
					</Breadcrumb.Section>
					<Breadcrumb.Divider icon="right chevron" />
					<Breadcrumb.Section>{t('activeSessions')}</Breadcrumb.Section>
				</Breadcrumb>
			}
			extra={
				<div className="ml-auto">
					<AccountProgress />
				</div>
			}
			flex
		>
			<MyMenu activeItem="agent">
				<Page.Wrap>
					<Segment>
						<div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
							<h3 style={{ margin: 0 }}>
								<Icon name="computer" />
								{t('activeSessions')}
							</h3>
							<Dropdown
								selection
								options={agentOptions}
								value={selectedUid}
								onChange={handleFilterChange}
								placeholder={t('allAgents')}
								style={{ minWidth: '250px' }}
							/>
						</div>

						{loading ? (
							<Page.Loader />
						) : sessions.length === 0 ? (
							<p style={{ color: '#999' }}>{t('noActiveSessions')}</p>
						) : (
							<Table>
								<Table.Header>
									<Table.Row>
										<Table.HeaderCell>{t('agent')}</Table.HeaderCell>
										<Table.HeaderCell>{t('device')}</Table.HeaderCell>
										<Table.HeaderCell>IP</Table.HeaderCell>
										<Table.HeaderCell>{t('loginDate')}</Table.HeaderCell>
										<Table.HeaderCell>{t('expiryDate')}</Table.HeaderCell>
										<Table.HeaderCell></Table.HeaderCell>
									</Table.Row>
								</Table.Header>
								<Table.Body>
									{sessions.map((s) => (
										<Table.Row key={s.sid}>
											<Table.Cell>
												<div style={{ fontWeight: 'bold' }}>{s.agentName || s.agentEmail}</div>
												<div style={{ color: '#999', fontSize: '0.85em' }}>{s.agentEmail}</div>
												<Label size="mini" color={roleColor(s.agentRole)}>
													{t(s.agentRole) || s.agentRole}
												</Label>
											</Table.Cell>
											<Table.Cell>
												<Icon name={deviceIcon(s.device)} />
												<div>{s.browser || t('unknownBrowser')}</div>
												<div style={{ color: '#666', fontSize: '0.85em' }}>{s.os || t('unknownOS')}</div>
											</Table.Cell>
											<Table.Cell>{s.ip || '-'}</Table.Cell>
											<Table.Cell>
												{s.loginAt ? new Date(s.loginAt).toLocaleString() : '-'}
											</Table.Cell>
											<Table.Cell>
												{s.expiredAt ? new Date(s.expiredAt).toLocaleString() : '-'}
											</Table.Cell>
											<Table.Cell textAlign="right">
												<Button
													size="tiny"
													color="red"
													basic
													loading={terminating === s.sid}
													onClick={() => handleTerminate(s.uid, s.sid)}
												>
													{t('terminateSession')}
												</Button>
											</Table.Cell>
										</Table.Row>
									))}
								</Table.Body>
							</Table>
						)}
					</Segment>
				</Page.Wrap>
			</MyMenu>
		</Page>
	);
}
